import { useEffect, useMemo, useRef, useState } from "react";
import Input from "../../../../ui/Input";
import Button from "../../../../ui/Button";
import Notification from "../../../../ui/Notification";
import {
  MdSave,
  MdUpload,
  MdDelete,
  MdChevronLeft,
  MdChevronRight,
  MdSwapHoriz,
} from "react-icons/md";
import {
  updateAdminContentText,
  uploadAdminContentMedia,
  replaceAdminContentMedia,
  deleteAdminContentMedia,
} from "../../../../api/adminContent.api";
import { resolveMediaUrl } from "../../../../utils/media";

const LANGS = ["en", "ar", "ku"];

const isVideo = (p = "") =>
  p.endsWith(".mp4") || p.endsWith(".webm");

const titleize = (s = "") =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const isMediaKey = (key = "") =>
  key.includes("gallery") ||
  key.includes("image") ||
  key.includes("slider") ||
  key.includes("media");

const ContentEditor = ({ items = [], onSaved }) => {
  const [activeLang, setActiveLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [form, setForm] = useState({});
  const [slideIndex, setSlideIndex] = useState({});
  const [pendingReplace, setPendingReplace] = useState(null);

  const replaceInputRefs = useRef({});
  const addInputRefs = useRef({});

  /* =========================
     BUILD FORM STATE
  ========================= */
  useEffect(() => {
    const obj = {};

    items.forEach((item) => {
      obj[item.id] = {
        key: item.key,
        media: item.media ?? [],
        values: Object.fromEntries(
          LANGS.map((l) => [
            l,
            item.translations?.find((t) => t.language === l)?.value ?? "",
          ])
        ),
      };
    });

    setForm(obj);
  }, [items]);

  const entries = useMemo(() => Object.entries(form), [form]);

  /* =========================
     TEXT
  ========================= */
  const handleTextChange = (id, value) => {
    setForm((p) => ({
      ...p,
      [id]: {
        ...p[id],
        values: { ...p[id].values, [activeLang]: value },
      },
    }));
  };

  /* =========================
     MEDIA ADD
  ========================= */
  const handleAddMedia = async (itemId, file) => {
    setSaving(true);
    try {
      const media = await uploadAdminContentMedia(itemId, file);

      setForm((p) => ({
        ...p,
        [itemId]: {
          ...p[itemId],
          media: [...p[itemId].media, media],
        },
      }));

      setSlideIndex((p) => ({ ...p, [itemId]: p[itemId]?.length ?? 0 }));
    } catch {
      setNotification({ type: "error", message: "Failed to add media" });
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     MEDIA DELETE
  ========================= */
  const handleRemoveMedia = async (itemId, mediaId) => {
    if (!window.confirm("Remove this media?")) return;

    setSaving(true);
    try {
      await deleteAdminContentMedia(itemId, mediaId);

      setForm((p) => ({
        ...p,
        [itemId]: {
          ...p[itemId],
          media: p[itemId].media.filter((m) => m.id !== mediaId),
        },
      }));

      setSlideIndex((p) => ({ ...p, [itemId]: 0 }));
    } catch {
      setNotification({ type: "error", message: "Failed to remove media" });
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     MEDIA REPLACE
  ========================= */
  const handleReplaceClick = (itemId, mediaId) => {
    setPendingReplace({ itemId, mediaId });
    replaceInputRefs.current[itemId]?.click();
  };

  const handleReplaceFileChange = async (itemId, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !pendingReplace) return;

    setSaving(true);
    try {
      const updated = await replaceAdminContentMedia(
        itemId,
        pendingReplace.mediaId,
        file
      );

      setForm((p) => ({
        ...p,
        [itemId]: {
          ...p[itemId],
          media: p[itemId].media.map((m) =>
            m.id === pendingReplace.mediaId ? { ...m, ...updated } : m
          ),
        },
      }));
    } catch {
      setNotification({ type: "error", message: "Failed to replace media" });
    } finally {
      setPendingReplace(null);
      setSaving(false);
    }
  };

  /* =========================
     SAVE TEXT
  ========================= */
  const handleSave = async () => {
    setSaving(true);

    try {
      for (const id of Object.keys(form)) {
        await updateAdminContentText(id, {
          translations: LANGS.map((l) => ({
            language: l,
            value: form[id].values[l],
          })),
        });
      }
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          type={notification.type}
          title="Media"
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* LANGUAGE SWITCH */}
      <div className="flex gap-2 border-b pb-2">
        {LANGS.map((l) => (
          <button
            key={l}
            onClick={() => setActiveLang(l)}
            className={`px-3 py-1 text-xs font-bold rounded ${
              activeLang === l
                ? "bg-[#56a5a9] text-white"
                : "bg-gray-100"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {entries.map(([id, item]) => {
        const showMedia = item.media.length > 0 || isMediaKey(item.key);
        const idx = Math.min(slideIndex[id] ?? 0, item.media.length - 1);
        const current = item.media[idx];

        return (
          <div key={id} className="border rounded-lg p-4 bg-gray-50 space-y-4">
            <div className="font-semibold">{titleize(item.key)}</div>

            {showMedia && (
              <>
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  ref={(el) => (replaceInputRefs.current[id] = el)}
                  onChange={(e) => handleReplaceFileChange(id, e)}
                />

                <div className="relative min-h-[200px] flex items-center justify-center bg-gray-200 rounded">
                  {current ? (
                    isVideo(current.filePath) ? (
                      <video
                        src={resolveMediaUrl(current.filePath)}
                        controls
                        className="max-h-[280px]"
                      />
                    ) : (
                      <img
                        src={resolveMediaUrl(current.filePath)}
                        className="max-h-[280px]"
                        alt=""
                      />
                    )
                  ) : (
                    <p className="text-gray-500">No media yet</p>
                  )}
                </div>

                {current && (
                  <div className="flex gap-4 text-sm">
                    <button
                      onClick={() => handleReplaceClick(id, current.id)}
                      className="text-[#56a5a9]"
                    >
                      <MdSwapHoriz /> Replace
                    </button>
                    <button
                      onClick={() => handleRemoveMedia(id, current.id)}
                      className="text-red-600"
                    >
                      <MdDelete /> Delete
                    </button>
                  </div>
                )}

                <label className="inline-flex items-center gap-2 text-[#56a5a9] cursor-pointer">
                  <MdUpload /> Add media
                  <input
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    ref={(el) => (addInputRefs.current[id] = el)}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleAddMedia(id, f);
                      e.target.value = "";
                    }}
                  />
                </label>
              </>
            )}

            <Input
              textarea
              rows={3}
              label={`${titleize(item.key)} (${activeLang})`}
              value={item.values[activeLang]}
              onChange={(e) => handleTextChange(id, e.target.value)}
            />
          </div>
        );
      })}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <MdSave className="mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default ContentEditor;
